import type {
  ActionCommand,
  EnterStockMode,
  MenuItem,
  SearchResultCommand,
  WorkflowState,
  WorkflowTransition
} from '@/domains/command-center/types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'

import { handleSelectParentProduct } from '@/domains/command-center/handlers/product/productCommands'

/**
 * Executes a command based on the selected menu item
 * @returns A Promise that resolves to the result of the command execution or undefined. An
 * `EnterStockMode` is handed back untouched: the dialog, not the command center, owns stock mode.
 */
const executeCommand = async (
  item: MenuItem,
  allCommands: (ActionCommand | SearchResultCommand)[],
  workflowState: WorkflowState,
  updateWorkflowState: (newState: WorkflowState) => void,
  clearQuery: () => void
): Promise<void | WorkflowTransition | EnterStockMode> => {
  // Input validation
  if (!item || !('id' in item) || !item.id) {
    return undefined
  }

  const commandToExecute = allCommands.find(cmd => cmd.id === item.id)

  if (!commandToExecute) {
    return undefined
  }

  // Check if disabled
  if ('disabled' in commandToExecute && commandToExecute.disabled) {
    return undefined
  }

  // Handle parent product selection workflow
  if (
    workflowState.mode === 'awaitingParentProduct' &&
    commandToExecute.type === 'searchResult' &&
    (commandToExecute as SearchResultCommand).resultType === 'product'
  ) {
    try {
      await handleSelectParentProduct(
        (commandToExecute as SearchResultCommand).originalData as IndexProduct,
        workflowState.pendingBarcodesForVariants,
        () => {
          updateWorkflowState({
            mode: 'idle',
            pendingBarcodesForVariants: []
          })
          clearQuery()
        }
      )
      return
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // Execute regular command
  if (commandToExecute.type === 'action' && typeof commandToExecute.execute === 'function') {
    const result = await Promise.resolve(commandToExecute.execute())

    // Handle workflow transitions. An `EnterStockMode` passes through: the dialog owns stock mode.
    if (result?.type === 'workflowTransition' && result.to === 'awaitingParentProduct') {
      updateWorkflowState({
        mode: 'awaitingParentProduct',
        pendingBarcodesForVariants: (result.data?.pendingBarcodesForVariants as string[]) || []
      })
      clearQuery()
    }

    return result
  }

  if (commandToExecute.type === 'searchResult' && typeof commandToExecute.execute === 'function') {
    return Promise.resolve(commandToExecute.execute())
  }

  // If no execution was triggered
  return Promise.resolve(undefined)
}

export { executeCommand }
